<script>
import WhatsappMessagePreview from 'dashboard/routes/dashboard/settings/campaigns/WhatsappMessagePreview.vue';
import FileUpload from 'vue-upload-component';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';

export default {
  components: {
    WhatsappMessagePreview,
    FileUpload,
    AttachmentPreview,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    category: {
      type: String,
      default: 'MARKETING',
    },
    language: {
      type: String,
      default: 'pt_BR',
    },
    header: {
      type: String,
      default: '',
    },
    footer: {
      type: String,
      default: '',
    },
    mediaType: {
      type: String,
      default: '',
    },
    file: {
      type: [Object, File],
      default: null,
    },
    body: {
      type: String,
      default: '',
    },
    buttons: {
      type: Array,
      default: () => [],
    },
    bodyVariableExamples: {
      type: Array,
      default: () => [],
    },
    detectedVariables: {
      type: Array,
      default: () => [],
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    languageOptions: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isFormValid: {
      type: Boolean,
      default: false,
    },
    previewTemplate: {
      type: Object,
      default: () => ({}),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    nameError: {
      type: Boolean,
      default: false,
    },
    bodyError: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:name',
    'update:category',
    'update:language',
    'update:header',
    'update:footer',
    'update:mediaType',
    'update:body',
    'update:buttons',
    'update:bodyVariableExamples',
    'submit',
    'cancel',
    'addButton',
    'removeButton',
    'update:file',
  ],
  data() {
    return {
      showNameFormatError: false,
      attachedFiles: [],
    };
  },
  computed: {
    mediaTypeOptions() {
      return [
        {
          value: '',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_TYPE_PLACEHOLDER'
          ),
        },
        {
          value: 'IMAGE',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_TYPES.IMAGE'
          ),
        },
        {
          value: 'VIDEO',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_TYPES.VIDEO'
          ),
        },
        {
          value: 'DOCUMENT',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_TYPES.DOCUMENT'
          ),
        },
      ];
    },
    showButtonsWarning() {
      return this.buttons.length > 3;
    },
    canAddMoreButtons() {
      return this.buttons.length < 10;
    },
    // Mutual exclusivity: header disabled when media is selected
    isHeaderDisabled() {
      return !!this.mediaType;
    },
    // Mutual exclusivity: media disabled when header has text or in edit mode
    isMediaDisabled() {
      return !!this.header || this.isEditMode;
    },
    // Next variable number to insert
    nextVariableNumber() {
      if (this.detectedVariables.length === 0) return 1;
      const numbers = this.detectedVariables.map(v => {
        const match = v.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      });
      return Math.max(...numbers) + 1;
    },
    allowedFileTypes() {
      if (this.mediaType === 'VIDEO') return 'video/mp4';
      if (this.mediaType === 'DOCUMENT') return 'application/pdf';
      if (this.mediaType === 'IMAGE') return 'image/png, image/jpeg';
      return '';
    },
    currentFileExtension() {
      if (this.mediaType === 'VIDEO') return '.mp4';
      if (this.mediaType === 'DOCUMENT') return '.pdf';
      if (this.mediaType === 'IMAGE') return '.png/.jpeg';
      return '';
    },
    mustUploadFile() {
      return !this.attachedFiles.length && this.mediaType;
    },
    uploadedFileIsNotInCorrectExtension() {
      const fileType = String(this.attachedFiles[0]?.resource?.file?.type);
      const correctExt = this.allowedFileTypes?.includes(fileType.trim());
      return !!this.attachedFiles.length && !correctExt;
    },
  },
  methods: {
    // Prevent form submission on Enter key press in input fields
    handleFormKeydown(event) {
      // Allow Enter only when the submit button is focused
      const isSubmitButton =
        event.target.type === 'submit' ||
        event.target.closest('button[type="submit"]');
      if (!isSubmitButton) {
        event.preventDefault();
      }
    },
    // Auto-format name: only lowercase, numbers, underscores
    handleNameInput(event) {
      let value = event.target.value;

      // Convert to lowercase
      value = value.toLowerCase();

      // Replace spaces with underscores
      value = value.replace(/ /g, '_');

      // Remove invalid characters (keep only a-z, 0-9, _)
      const cleanedValue = value.replace(/[^a-z0-9_]/g, '');

      // Check if there were invalid characters
      if (cleanedValue !== value) {
        this.showNameFormatError = true;
        // Use the cleaned value
        value = cleanedValue;
      } else {
        this.showNameFormatError = false;
      }

      this.$emit('update:name', value);

      // Update the input field directly to show the formatted value
      event.target.value = value;
    },
    // Handle keydown for space -> underscore conversion
    handleNameKeydown(event) {
      if (event.key === ' ') {
        event.preventDefault();
        const input = event.target;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const currentValue = input.value;

        // Insert underscore at cursor position
        const newValue =
          currentValue.slice(0, start) + '_' + currentValue.slice(end);
        this.$emit('update:name', newValue);

        // Restore cursor position after underscore
        this.$nextTick(() => {
          input.setSelectionRange(start + 1, start + 1);
        });
      }
    },
    updateButton(index, value) {
      const newButtons = [...this.buttons];
      newButtons[index] = value;
      this.$emit('update:buttons', newButtons);
    },
    updateVariableExample(index, value) {
      const newExamples = [...this.bodyVariableExamples];
      newExamples[index] = value;
      this.$emit('update:bodyVariableExamples', newExamples);
    },
    getMediaTypeLabel(value) {
      const option = this.mediaTypeOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    },
    // Add variable to body
    addVariableToBody() {
      const variableTag = `{{${this.nextVariableNumber}}}`;
      const newBody = this.body + variableTag;
      this.$emit('update:body', newBody);
    },
    // Handle header input - clear media if header is typed
    handleHeaderInput(event) {
      const value = event.target.value;
      this.$emit('update:header', value);
      // If user starts typing header, clear media selection
      if (value && this.mediaType) {
        this.$emit('update:mediaType', '');
      }
    },
    // Handle media change - clear header if media is selected
    handleMediaChange(event) {
      const value = event.target.value;
      this.$emit('update:mediaType', value);
      // If user selects media, clear header
      if (value && this.header) {
        this.$emit('update:header', '');
      }
      // Reset file when media type changes
      this.attachedFiles = [];
      this.$emit('update:file', null);
    },
    onFileUpload(file) {
      if (!file) return;
      // You might want to add file size/type validation here if needed,
      // though vue-upload-component handles basic checks via props.
      this.attachFile({ file });
    },
    attachFile({ blob, file }) {
      this.attachedFiles = [];
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
        this.$emit('update:file', this.attachedFiles[0]?.resource?.file);
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
      this.$emit('update:file', null);
    },
    // Apply text formatting (bold, italic, strikethrough, monospace)
    applyFormatting(formatType) {
      const textarea = this.$refs.bodyTextarea;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.body.substring(start, end);

      let prefix;
      let suffix;
      switch (formatType) {
        case 'bold':
          prefix = '*';
          suffix = '*';
          break;
        case 'italic':
          prefix = '_';
          suffix = '_';
          break;
        case 'strikethrough':
          prefix = '~';
          suffix = '~';
          break;
        case 'monospace':
          prefix = '```';
          suffix = '```';
          break;
        default:
          return;
      }

      const beforeSelection = this.body.substring(0, start);
      const afterSelection = this.body.substring(end);

      let newBody;
      let newCursorPosition;

      if (selectedText) {
        // Wrap selected text with formatting
        newBody =
          beforeSelection + prefix + selectedText + suffix + afterSelection;
        newCursorPosition =
          start + prefix.length + selectedText.length + suffix.length;
      } else {
        // Insert formatting characters and position cursor between them
        newBody = beforeSelection + prefix + suffix + afterSelection;
        newCursorPosition = start + prefix.length;
      }

      this.$emit('update:body', newBody);

      // Restore focus and set cursor position
      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col md:flex-row gap-6 p-6 h-full bg-white dark:bg-slate-900"
  >
    <!-- Form -->
    <form
      class="flex-1 flex flex-col gap-4 min-w-0 overflow-y-auto pr-2"
      @submit.prevent="$emit('submit')"
      @keydown.enter="handleFormKeydown"
    >
      <!-- Name -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.NAME_LABEL') }}
        </label>
        <input
          :value="name"
          type="text"
          :disabled="isEditMode"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 outline-none transition-shadow"
          :class="{
            'border-red-500': nameError || showNameFormatError,
            'dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed bg-gray-50 mb-1':
              isEditMode,
            'dark:bg-gray-800': !isEditMode,
          }"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.NAME_PLACEHOLDER'
            )
          "
          @input="handleNameInput"
          @keydown="handleNameKeydown"
        />
        <p
          v-if="!isEditMode && !showNameFormatError"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.NAME_HELP') }}
        </p>
        <p v-if="showNameFormatError" class="mt-1 text-xs text-red-500">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.NAME_HELP') }}
        </p>
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span
          v-if="isEditMode"
          class="text-xs text-yellow-600 dark:text-yellow-500"
        >
          *
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.NAME_READONLY') }}
        </span>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Category -->
        <div>
          <label
            class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.CATEGORY_LABEL'
              )
            }}
          </label>
          <select
            :value="category"
            :disabled="isEditMode"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:text-gray-100"
            :class="{
              'dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed bg-gray-50':
                isEditMode,
              'dark:bg-gray-800': !isEditMode,
            }"
            @change="$emit('update:category', $event.target.value)"
          >
            <option
              v-for="option in categoryOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Language -->
        <div>
          <label
            class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.LANGUAGE_LABEL'
              )
            }}
          </label>
          <select
            :value="language"
            :disabled="isEditMode"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:text-gray-100"
            :class="{
              'dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed bg-gray-50':
                isEditMode,
              'dark:bg-gray-800': !isEditMode,
            }"
            @change="$emit('update:language', $event.target.value)"
          >
            <option
              v-for="option in languageOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Media -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_LABEL') }}
        </label>
        <select
          :value="mediaType"
          :disabled="isMediaDisabled"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:text-gray-100"
          :class="{
            'dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed bg-gray-50':
              isMediaDisabled,
            'dark:bg-gray-800': !isMediaDisabled,
          }"
          @change="handleMediaChange"
        >
          <option
            v-for="option in mediaTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <!-- File Upload Section -->
        <div v-if="mediaType && !isEditMode" class="mt-2">
          <FileUpload
            ref="upload"
            input-id="hsmAttachment"
            :size="4096 * 4096"
            :accept="allowedFileTypes"
            :multiple="false"
            drop
            :drop-directory="false"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            @input-file="onFileUpload"
          >
            <woot-button
              v-if="true"
              :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
              icon="attach"
              emoji="📎"
              color-scheme="secondary"
              variant="smooth"
              :class="{ 'border-red-500 border': mustUploadFile }"
            >
              {{
                $t('WHATSAPP_TEMPLATES.PARSER.ATTACH') +
                ` ${currentFileExtension}`
              }}
            </woot-button>
          </FileUpload>

          <div v-if="attachedFiles.length">
            <AttachmentPreview
              class="flex-col mt-4"
              :attachments="attachedFiles"
              :remove-attachment="removeAttachment"
            />
          </div>
          <p v-else class="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.SUBMIT_FILE') }}
          </p>
        </div>

        <p
          v-if="!isEditMode && !header && !mediaType"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_HELP') }}
        </p>
        <p
          v-if="header && !isEditMode"
          class="mt-1 text-xs text-yellow-600 dark:text-yellow-500"
        >
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.MEDIA_HEADER_EXCLUSIVE'
            )
          }}
        </p>
      </div>

      <!-- Header -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.HEADER_LABEL')
          }}
        </label>
        <input
          :value="header"
          type="text"
          :disabled="isHeaderDisabled"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 outline-none transition-shadow"
          :class="{
            'dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed bg-gray-50':
              isHeaderDisabled,
            'dark:bg-gray-800': !isHeaderDisabled,
          }"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.HEADER_PLACEHOLDER'
            )
          "
          @input="handleHeaderInput"
        />
        <p
          v-if="!mediaType"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.HEADER_HELP') }}
        </p>
        <p
          v-if="mediaType"
          class="mt-1 text-xs text-yellow-600 dark:text-yellow-500"
        >
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.HEADER_MEDIA_EXCLUSIVE'
            )
          }}
        </p>
      </div>

      <!-- Body -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BODY_LABEL') }}
        </label>
        <textarea
          ref="bodyTextarea"
          :value="body"
          rows="4"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BODY_PLACEHOLDER'
            )
          "
          :class="{ 'border-red-500': bodyError }"
          @input="$emit('update:body', $event.target.value)"
        />
        <div class="flex items-center justify-end mt-1 gap-1">
          <button
            type="button"
            class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1"
            @click="addVariableToBody"
          >
            <fluent-icon icon="add" size="12" />
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.ADD_VARIABLE_BUTTON'
              )
            }}
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            :title="
              $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.FORMAT_BOLD')
            "
            @click="applyFormatting('bold')"
          >
            B
          </button>
          <button
            type="button"
            class="px-2 py-1 text-xs italic text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            :title="
              $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.FORMAT_ITALIC')
            "
            @click="applyFormatting('italic')"
          >
            I
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.FOOTER_LABEL')
          }}
        </label>
        <input
          :value="footer"
          type="text"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 outline-none transition-shadow"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.FOOTER_PLACEHOLDER'
            )
          "
          @input="$emit('update:footer', $event.target.value)"
        />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.FOOTER_HELP') }}
        </p>
      </div>

      <!-- Variable Examples -->
      <div v-if="detectedVariables.length > 0">
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.VARIABLES_LABEL')
          }}
        </label>
        <p
          v-if="!isEditMode"
          class="mb-2 text-xs text-gray-500 dark:text-gray-400"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.VARIABLES_HELP')
          }}
        </p>
        <div class="flex flex-col gap-2">
          <div
            v-for="(variable, index) in detectedVariables"
            :key="variable"
            class="flex items-center gap-2"
          >
            <span
              class="w-12 text-sm font-medium text-gray-600 dark:text-gray-300 shrink-0"
            >
              {{ variable }}
            </span>
            <input
              :value="bodyVariableExamples[index]"
              type="text"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              :placeholder="`Example for ${variable}`"
              @input="updateVariableExample(index, $event.target.value)"
            />
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div>
        <label
          class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTONS_LABEL')
          }}
        </label>
        <p
          v-if="!isEditMode"
          class="mb-2 text-xs text-gray-500 dark:text-gray-400"
        >
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTONS_HELP')
          }}
        </p>
        <!-- Warning for more than 3 buttons -->
        <div
          v-if="showButtonsWarning"
          class="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md"
        >
          <p
            class="text-sm text-yellow-800 dark:text-yellow-200 flex items-start gap-2"
          >
            <fluent-icon icon="warning" size="16" class="mt-0.5 shrink-0" />
            <span>
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTONS_WARNING'
                )
              }}
            </span>
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(button, index) in buttons"
            :key="index"
            class="flex items-center gap-2"
          >
            <input
              :value="button"
              type="text"
              class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTONS_PLACEHOLDER'
                )
              "
              @input="updateButton(index, $event.target.value)"
            />
            <button
              type="button"
              class="p-2 text-gray-400 hover:text-red-500"
              @click="$emit('removeButton', index)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>
        <button
          v-if="canAddMoreButtons"
          type="button"
          class="mt-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
          @click="$emit('addButton')"
        >
          {{
            '+ ' +
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.BUTTONS_ADD')
          }}
        </button>
      </div>

      <!-- Submit -->
      <div
        class="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-900 z-10"
      >
        <woot-button variant="clear" @click="$emit('cancel')">
          {{ $t('INBOX_MGMT.TEIMOSINHA.CREATE_MESSAGE.BUTTONS.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isLoading"
          :disabled="!isFormValid"
        >
          {{
            isEditMode
              ? $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.SUBMIT')
              : $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.SUBMIT')
          }}
        </woot-button>
      </div>
    </form>

    <!-- Preview -->
    <div
      class="w-full md:w-96 flex-shrink-0 border-t md:border-t-0 md:border-l border-gray-100 dark:border-gray-700 md:pl-6 pt-6 md:pt-0"
    >
      <h4
        class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block sticky top-0 bg-white dark:bg-slate-900 z-10 pb-2"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.PREVIEW_SECTION') }}
      </h4>
      <div class="sticky top-10">
        <WhatsappMessagePreview :template="previewTemplate" />
      </div>
    </div>
  </div>
</template>
